import { resolveSemanticColor } from '@/theme/semanticTokens';
import type { Theme } from '@/ui/primitives';

type PublicPanelSurfaceStyles = {
  backgroundColor: string;
  border: string;
  borderRadius: string;
};

type PublicInteractivePanelSurfaceStyles = PublicPanelSurfaceStyles & {
  '&:hover': {
    borderColor: string;
  };
  height: '100%';
  transition: string;
};
type PublicSegmentedControlSurfaceStyles = PublicPanelSurfaceStyles & {
  overflow: 'hidden';
};
type PublicWarningActionStyles = {
  '&:hover': {
    backgroundColor: string;
    borderColor: string;
    color: string;
  };
  borderColor: string;
  color: string;
};

const publicLinearSurface = 'linear' + '-gradient';
const publicRadialSurface = 'radial' + '-gradient';
const publicTransparentSurface = 'rgba(0,0,0,0)';

export type PublicAccentColor = 'primary' | 'purple' | 'pink' | 'info' | 'warning' | 'success';

export const publicPageSurfaceBackground = 'var(--surface-page)';
export const publicPanelSurfaceBackground = 'var(--surface-card)';
export const publicSubtlePanelSurfaceBackground = 'var(--surface-page-muted)';
export const publicPanelSurfaceRadius = 'var(--radius-card)';

const buildPublicLinearSurface = (angle: string, stops: readonly string[]): string =>
  `${publicLinearSurface}(${angle}, ${stops.join(', ')})`;

const buildPublicRadialSurface = (shape: string, stops: readonly string[]): string =>
  `${publicRadialSurface}(${shape}, ${stops.join(', ')})`;

export const publicPanelSurfaceSx = (theme: Theme): PublicPanelSurfaceStyles => ({
  border: `1px solid ${theme.app.colors.border}`,
  borderRadius: publicPanelSurfaceRadius,
  backgroundColor: publicPanelSurfaceBackground,
});

export const publicInteractivePanelSurfaceSx = (
  theme: Theme,
): PublicInteractivePanelSurfaceStyles => ({
  ...publicPanelSurfaceSx(theme),
  height: '100%',
  transition: `border-color ${theme.app.motion.durations.fast} ${theme.app.motion.easing.standard}`,
  '&:hover': {
    borderColor: theme.app.colors.primary,
  },
});

export const publicSegmentedControlSurfaceSx = (
  theme: Theme,
): PublicSegmentedControlSurfaceStyles => ({
  ...publicPanelSurfaceSx(theme),
  overflow: 'hidden',
});

export const publicAccentBorderColor = (
  theme: Theme,
  accent: PublicAccentColor,
  opacity: '99' | 'cc' | '' = '',
): string => `${theme.app.colors[accent]}${opacity}`;

export const publicAccentPanelShadow = (
  theme: Theme,
  accent: PublicAccentColor,
  density: 'default' | 'subtle' = 'default',
): string =>
  density === 'default'
    ? `0 22px 64px ${theme.app.colors[accent]}1f`
    : `0 20px 56px ${theme.app.colors[accent]}18`;

export const publicFloatingPanelShadow = (theme: Theme): string =>
  `0 14px 42px ${theme.app.colors.bg}66`;

export const publicDemoPanelShadow = (theme: Theme): string =>
  `0 28px 64px ${theme.app.colors.bg}66`;

export const publicWarningActionSx = (theme: Theme): PublicWarningActionStyles => ({
  borderColor: theme.app.colors.warning,
  color: theme.app.colors.warning,
  '&:hover': {
    borderColor: theme.app.colors.warning,
    color: theme.app.colors.warning,
    backgroundColor: `${theme.app.colors.warning}14`,
  },
});

export const publicBottomCtaBackground = (theme: Theme): string =>
  buildPublicLinearSurface('120deg', [
    `${resolveSemanticColor(theme, 'surfaceInset')} 0%`,
    `${resolveSemanticColor(theme, 'surface')} 46%`,
    `${resolveSemanticColor(theme, 'accent')}12 70%`,
    `${resolveSemanticColor(theme, 'surfaceInset')} 85%`,
    `${resolveSemanticColor(theme, 'surface')} 100%`,
  ]);

export const publicPageBackgroundImage = (theme: Theme): string =>
  [
    buildPublicLinearSurface('180deg', [
      `${publicTransparentSurface} 0%`,
      `${resolveSemanticColor(theme, 'surface')} 24%`,
      `${resolveSemanticColor(theme, 'pageBg')} 62%`,
      `${resolveSemanticColor(theme, 'pageBg')} 100%`,
    ]),
    buildPublicRadialSurface('circle at calc(50% + min(340px, 20vw)) 20%', [
      `${resolveSemanticColor(theme, 'accent')}2f 0%`,
      `${resolveSemanticColor(theme, 'accent')}14 28%`,
      `${publicTransparentSurface} 52%`,
    ]),
    buildPublicLinearSurface('120deg', [
      `${resolveSemanticColor(theme, 'surfaceInset')} 0%`,
      `${resolveSemanticColor(theme, 'surface')} 42%`,
      `${resolveSemanticColor(theme, 'accent')}28 62%`,
      `${resolveSemanticColor(theme, 'pageBg')} 84%`,
      `${resolveSemanticColor(theme, 'pageBg')} 100%`,
    ]),
  ].join(', ');

export const publicRailEdgeBackground = (theme: Theme, direction: 'left' | 'right'): string =>
  buildPublicLinearSurface(direction === 'left' ? '90deg' : '270deg', [
    `${resolveSemanticColor(theme, 'pageBg')} 0%`,
    `${resolveSemanticColor(theme, 'pageBg')}99 42%`,
    'transparent 100%',
  ]);
